import { useUnmount } from 'ahooks';
import React, { useEffect, useState } from 'react';
import { Tabs } from '@meitu/saas-ui';
import { type Editor } from '@meitu/saas-editor-core';
import { LayerType } from '@meitu/saas-editor-core/es/version-types';
import { BaseToolbar } from '@meitu/saas-editor-ui/es/components/base-toolbar';
import { DividerLine } from '@meitu/saas-editor-ui/es/components/divider-line';
import { type FontItem } from '@meitu/saas-editor-ui/es/components/font-family-selector';
import { HeaderToolbar } from '@meitu/saas-editor-ui/es/components/header-toolbar';
import LabelAction from '@meitu/saas-editor-ui/es/components/label-action';
import { useBaseTool, useHeaderTool } from '@meitu/saas-editor-ui/es/hooks/use-common-tool';
import { EditorContext } from '@meitu/saas-editor-ui/es/hooks/use-editor';
import { TextPanelContext } from '@meitu/saas-editor-ui/es/panels/text-layer/context';
import { useShapeTextCache } from '@meitu/saas-editor-ui/es/panels/text-layer/hooks';
import { TextEffectPanel } from '@meitu/saas-editor-ui/es/panels/text-layer/panels';
import { type ShapeTextMaterialRaw, type TexturePanelRenderFn } from '@meitu/saas-editor-ui/es/panels/text-layer/types';
import {
    ArtTextEntry,
    FontFamily,
    FontSize,
    FontStyle,
    LayerOpacity,
    ListStyle,
    ShapeTextEntry,
    TextAlignment,
    TextColorPicker,
    TextEffectEntry,
    TextMarkEntry,
    TextSpacing,
} from '@meitu/saas-editor-ui/es/panels/text-layer/widgets';
import TextEffectController from '@meitu/saas-editor-ui/es/panels/text-layer/widgets/text-effect-controller';
import { type ArtTextStruct, type ListStyleStruct } from '@meitu/saas-editor-ui/es/types';
import { isEditAIPPTComponent, isEditAIPPTTemplate, isEditTextArt } from '@/hooks/fetch-template/editor-mode';
import { usePanelLayerId } from '@/hooks/use-panel-layer-id';
import { useLocaleStore } from '@/i18n';
import { editor } from '@/instance';
import { isIntl } from '@/react-process-env';
import { toWidgetFormat } from '@/services/font';
import { useStoreState } from '@/store';
import PPTMarkPanel from '../ppt-mark-panel';
import { TextArtConfig } from '../text-art-panel/text-art-panel';
import { useShapeTextData } from './hooks';
import { TextAnimationEntry } from './widgets';
import '@meitu/saas-editor-ui/es/panels/text-layer/style.less';
import '@meitu/saas-editor-ui/es/panels/text-layer/shared.less';
import './style.less';

type Props = {
    /**
     * 应用层主题
     */
    theme: string;
    /**
     * 是否是国际化环境
     */
    isIntl: boolean;
    /**
     * 编辑器实例
     */
    editor: Editor;
    /**
     * 当前编辑的图层 id
     */
    layerId: string;
    /**
     * 字体列表
     */
    fonts: FontItem[];
    /**
     * 变形文字列表
     */
    shapeTextList: ShapeTextMaterialRaw[];
    /**
     * 列表样式列表
     */
    listStyles: ListStyleStruct[];
    /**
     * 新版花字特效列表
     */
    artTexts?: ArtTextStruct[];
    /**
     * 渲染纹理面板渲染问题（// FIXME: 历史问题，暂时先以当前形式实现）
     */
    renderTexturePanel: TexturePanelRenderFn;
    /**
     * 渲染品牌库文字弹窗触发器
     */
    renderBrandTextsPopupTrigger: () => JSX.Element | null;
    /**
     * 是否显示划重点入口
     */
    showTextMarkEntry: boolean;
    /**
     * 是否显示 AI 花字入口
     */
    showArtTextEntry: boolean;
    /**
     * 是否显示特效预设列表
     */
    showEffectPresets?: boolean;
    /**
     * 免费使用文字特效素材
     */
    freeTextEffect?: boolean;
    /**
     * 应用层作图记录 id，主要用于埋点
     */
    recordId?: number;
    /**
     * 是否是 VIP 用户
     */
    isVIP?: boolean;
    /**
     * 打开 VIP 弹窗
     */
    onOpenVIPModal?: () => void;
    /**
     * 打开 AI 花字
     */
    onOpenArtText?: () => void;
};

const TextLayerPanel: React.FC<Props> = ({
    theme,
    isIntl,
    editor,
    layerId,
    fonts,
    shapeTextList,
    listStyles,
    artTexts,
    renderTexturePanel,
    renderBrandTextsPopupTrigger,
    showArtTextEntry,
    showTextMarkEntry,
    showEffectPresets = true,
    recordId,
    freeTextEffect = false,
    isVIP = false,
    onOpenVIPModal,
    onOpenArtText,
}) => {
    const { $t } = useLocaleStore();
    const headerToolCallback = useHeaderTool(editor, 1, recordId);
    const baseTrackCallback = useBaseTool(editor, 1, recordId);

    const [subPanel, setSubPanel] = useState<string | null>(null);
    const [isGroupLayer, setIsGroupLayer] = useState(false);
    const [subAttrType, setSubAttrType] = useState<string | null>(null);
    const textArtMode = isEditTextArt();

    const hasFuncEntry =
        (!isGroupLayer && (showTextMarkEntry || showArtTextEntry)) || (showArtTextEntry && onOpenArtText);

    const onOpenSubPanel = (subPanel: string, type?: string) => {
        setSubPanel(subPanel);
        if (type) setSubAttrType(type);
    };

    const closeSubPanel = () => {
        setSubPanel(null);
        setSubAttrType(null);
    };

    const handleOpenArtText = () => {
        if (onOpenArtText) onOpenArtText();
    };

    useShapeTextCache(layerId);

    useEffect(() => {
        const layer = editor.active.getPick();

        const __isGroupLayer = layer?.layerType === LayerType.GROUP_LAYER;

        setIsGroupLayer(__isGroupLayer);
    }, [layerId]);

    // HACK: 文字编辑层的时候有在 mousedown 事件阻止冒泡
    // 但是阻止冒泡之后，点击文字编辑框就会影响弹出层自动收起
    // 后尝试在进入编辑态时调用 disableMouseSelect，退出编辑时调用 enableMouseSelect 发现不生效（disableMouseSelect 后仍可以拉动选框）
    // 郑鹏反馈这个问题不太好改，暂时先在业务方进行 HACK
    useEffect(() => {
        const dispose = editor.textEdit.onMouseDown(() => {
            const body = document.body;

            if (body) {
                const clickEvent = new MouseEvent('mousedown', {
                    bubbles: true,
                    cancelable: true,
                    view: window,
                });

                body.dispatchEvent(clickEvent);
            }
        });
        return () => dispose();
    }, []);

    useUnmount(() => {
        const editingLayer = editor.textEdit.getEditingLayer();
        if (editingLayer && editingLayer.id === layerId) editor.textEdit.exitEditing();
    });

    const textPanelContextValue = {
        layerId,
        recordId,
        theme,
        isIntl,
        listStyles,
        artTexts,
        showArtTextEntry,
        onOpenArtText,
        isVIP,
        onOpenVIPModal,
        onOpenSubPanel,
    };

    return (
        <EditorContext.Provider value={editor}>
            <TextPanelContext.Provider value={textPanelContextValue}>
                <div className="text-layer-panel">
                    <HeaderToolbar
                        id={layerId}
                        editor={editor}
                        style={{ padding: 16 }}
                        BrandTextsPopup={renderBrandTextsPopupTrigger}
                        {...headerToolCallback}
                    />

                    <div className="text-layer-panel__content">
                        <div className="text-layer-panel__block">
                            <FontFamily fonts={fonts} enableRemoteSearch={false} />
                            <LabelAction title="字号" align>
                                <div className="text-layer-panel__ctrl">
                                    <FontSize />
                                    <TextColorPicker renderTexturePanel={renderTexturePanel} />
                                </div>
                            </LabelAction>
                            <FontStyle />
                            <TextAlignment />
                            <LabelAction title="间距" align>
                                <div className="text-layer-panel__ctrl">
                                    <TextSpacing />
                                    {!isGroupLayer ? <ListStyle /> : null}
                                </div>
                            </LabelAction>
                        </div>

                        <DividerLine />

                        {!textArtMode ? (
                            <div
                                className="text-layer-panel__block"
                                id="text-layer-panel__block"
                                style={{ flexDirection: 'row' }}
                            >
                                {!isGroupLayer ? (
                                    <>
                                        {/* 划重点入口 */}
                                        {showTextMarkEntry ? <TextMarkEntry /> : null}

                                        {/* 变形文字入口 */}
                                        <ShapeTextEntry shapeTextList={shapeTextList} />
                                    </>
                                ) : null}

                                {showArtTextEntry && onOpenArtText ? (
                                    <ArtTextEntry onClick={handleOpenArtText} />
                                ) : null}
                            </div>
                        ) : null}

                        {hasFuncEntry && !textArtMode && <DividerLine />}

                        {/* 文字特效入口 */}
                        <div className="text-layer-panel__block">
                            {!textArtMode ? (
                                <TextEffectEntry
                                    free={freeTextEffect}
                                    hidePresets={!showEffectPresets}
                                    onClick={() => onOpenSubPanel('textEffect')}
                                />
                            ) : null}
                            <TextEffectController />
                            <LayerOpacity />
                        </div>

                        <DividerLine />

                        {!textArtMode ? (
                            <>
                                {/* 文字动效入口 */}
                                <div className="text-layer-panel__block">
                                    <TextAnimationEntry layerId={layerId} />
                                </div>
                                <DividerLine />

                                {/* 通用工具栏 */}
                                <BaseToolbar
                                    id={layerId}
                                    editor={editor}
                                    style={{ padding: '0 16px 16px' }}
                                    {...baseTrackCallback}
                                />
                            </>
                        ) : (
                            <div className="text-layer-panel__block">
                                <TextArtConfig layerId={layerId} />
                            </div>
                        )}
                    </div>
                </div>
                {subPanel === 'textEffect' ? (
                    <TextEffectPanel
                        type={subAttrType}
                        free={freeTextEffect}
                        hidePresets={!showEffectPresets}
                        onExit={closeSubPanel}
                    />
                ) : null}
            </TextPanelContext.Provider>
        </EditorContext.Provider>
    );
};

// FIXME: 涉及到 color-group 传入纹理面板的相关操作过于奇葩，后面需要重构

const pptMarkTabsList = [
    {
        name: '画笔',
        tabKey: 'bg',
    },
    {
        name: 'ppt打标',
        tabKey: 'pptMark',
    },
];

const RenderTabs: React.FC<{
    showMaskContent: boolean;
    setShowMaskContent: (flag: boolean) => void;
    isPPT?: boolean;
}> = ({ isPPT = false, setShowMaskContent }) => {
    const [tabIndex, setTabIndex] = useState(1);
    const handleTabClick = (key: string) => {
        setTabIndex(key === 'bg' ? 1 : 2);
        setShowMaskContent(key !== 'bg');
    };

    return (
        <div style={{ padding: '0 var(--gap-lg)' }}>
            <Tabs
                showContent={false}
                data={isPPT ? pptMarkTabsList : []}
                tabsWidth="228px"
                tabsHeight="34px"
                handleTabClick={handleTabClick}
                activeIndex={tabIndex}
            />
        </div>
    );
};

const isAIPPTMark = isEditAIPPTComponent() || isEditAIPPTTemplate();

const TextPanel: React.FC = () => {
    const layerId = usePanelLayerId();
    const shapeTextList = useShapeTextData();

    const fonts = useStoreState((state) => state.fontMaterialModel.fonts);
    const theme = useStoreState((state) => state.editorModel.theme);
    const recordInfo = useStoreState((state) => state.recordModel.recordInfo);
    const customParams = useStoreState((state) => state.sdkModel.customParams);
    const [showMaskContent, setShowMaskContent] = useState(false);
    const listStyles = useStoreState((state) => state.fontMaterialModel.listStyles);
    const artTexts = useStoreState((state) => state.fontMaterialModel.artTexts);

    if (!layerId) return null;

    return (
        <div>
            {isAIPPTMark ? (
                <div style={{ marginTop: '16px', marginBottom: '16px' }}>
                    <RenderTabs
                        showMaskContent={showMaskContent}
                        setShowMaskContent={setShowMaskContent}
                        isPPT={isAIPPTMark}
                    />
                </div>
            ) : null}

            <div
                style={{
                    display: `${isAIPPTMark && showMaskContent ? 'none' : 'block'}`,
                }}
            >
                <TextLayerPanel
                    listStyles={listStyles}
                    artTexts={artTexts}
                    theme={theme}
                    isIntl={isIntl}
                    editor={editor}
                    layerId={layerId}
                    recordId={recordInfo?.id}
                    isVIP={true}
                    fonts={toWidgetFormat(fonts)}
                    shapeTextList={shapeTextList}
                    showTextMarkEntry={!customParams.hideMask}
                    showArtTextEntry={false}
                    showEffectPresets={!customParams.hideTextEffect}
                    freeTextEffect={customParams.textEffectFree}
                    renderBrandTextsPopupTrigger={() => <></>}
                    renderTexturePanel={() => <></>}
                />
            </div>

            {isAIPPTMark && showMaskContent ? <PPTMarkPanel editor={editor} key={layerId} /> : null}
        </div>
    );
};

export default React.memo(TextPanel);
